import { NotificationEntity, NotificationSettingEntity, NotificationType } from '../entities';
import { IGetNotificationsResponse } from './types';

// The exact response envelopes aren't pinned down in the brief, so every parser
// below is defensive: it accepts the documented shape (`data` + `unreadCount`)
// as well as common alternatives (Spring `Page`, bare numbers, keyed maps).

const NOTIFICATION_TYPES = Object.values(NotificationType) as string[];

const toBool = (value: unknown): boolean => value === true || value === 'true';

export const normalizeCount = (resp: unknown): number => {
  if (typeof resp === 'number') return resp;
  if (resp && typeof resp === 'object') {
    const r = resp as Record<string, unknown>;
    const raw = r.unreadCount ?? r.count ?? r.total ?? r.value ?? 0;
    const num = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(num) ? num : 0;
  }
  return 0;
};

export const normalizeNotification = (n: Record<string, any>): NotificationEntity => {
  const params: Record<string, any> = n.params ?? {};

  return {
    id: n.id,
    type: n.type,
    // Backend may express read-state as `read` or `unread`.
    read: typeof n.read === 'boolean' ? n.read : n.unread === true ? false : Boolean(n.read),
    createdAt: n.createdAt ?? n.created_at ?? n.timestamp ?? '',
    droneName: n.droneName ?? params.droneName ?? n.drone?.name ?? (typeof n.drone === 'string' ? n.drone : undefined),
    droneSerial: n.droneSerial ?? params.droneSerial ?? n.drone?.serialNumber,
    droneId: n.droneId ?? params.droneId ?? n.drone?.id,
    orderId: n.orderId ?? params.orderId ?? n.order?.id ?? n.orderNumber,
  };
};

export const normalizeList = (resp: any): IGetNotificationsResponse => {
  const items = resp?.data ?? resp?.content ?? resp?.notifications ?? [];
  const data = Array.isArray(items) ? items.map(normalizeNotification) : [];
  const totalCount =
    resp?.totalCount ?? resp?.totalElements ?? resp?.total ?? data.length;
  return {
    data,
    unreadCount: normalizeCount(resp),
    totalCount: typeof totalCount === 'number' ? totalCount : Number(totalCount) || data.length,
  };
};

export const normalizeSettings = (resp: any): NotificationSettingEntity[] => {
  const raw = resp?.settings ?? resp;
  if (Array.isArray(raw)) {
    return raw
      .filter((s) => s && NOTIFICATION_TYPES.includes(s.type))
      .map((s) => ({ type: s.type as NotificationType, enabled: toBool(s.enabled ?? s.value ?? s.on) }));
  }
  if (raw && typeof raw === 'object') {
    return Object.entries(raw)
      .filter(([key]) => NOTIFICATION_TYPES.includes(key))
      .map(([type, enabled]) => ({ type: type as NotificationType, enabled: toBool(enabled) }));
  }
  return [];
};
